import {
  computeMenuPosition,
  wasClickOutside,
  log,
} from '@livekit/components-core';
import React from 'react';
import { MediaDeviceSelect } from './MediaDeviceSelect';

export function MediaDeviceMenu({
  kind,
  initialSelection,
  onActiveDeviceChange,
  tracks,
  requestPermissions = false,
  ...props
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [devices, setDevices] = React.useState([]);
  const [updateRequired, setUpdateRequired] = React.useState(true);
  const [needPermissions, setNeedPermissions] =
    React.useState(requestPermissions);

  const handleActiveDeviceChange = (deviceKind, deviceId) => {
    log.debug('handle device change');
    setIsOpen(false);
    if (onActiveDeviceChange) {
      onActiveDeviceChange(deviceKind, deviceId);
    }
  };

  const button = React.useRef(null);
  const tooltip = React.useRef(null);

  React.useLayoutEffect(() => {
    if (isOpen) {
      setNeedPermissions(true);
    }
  }, [isOpen]);

  React.useLayoutEffect(() => {
    if (button.current && tooltip.current && (devices || updateRequired)) {
      computeMenuPosition(button.current, tooltip.current).then(({ x, y }) => {
        if (tooltip.current) {
          Object.assign(tooltip.current.style, {
            left: `${x}px`,
            top: `${y}px`,
          });
        }
      });
    }
    setUpdateRequired(false);
  }, [devices, updateRequired]);

  const handleClickOutside = React.useCallback(
    (event) => {
      if (!tooltip.current) return;
      if (event.target === button.current) return;
      if (isOpen && wasClickOutside(tooltip.current, event)) {
        setIsOpen(false);
      }
    },
    [isOpen]
  );

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    const handleResize = () => setUpdateRequired(true);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleClickOutside]);

  return (
    <>
      {!props.disabled && (
        <>
          {kind ? (
            <MediaDeviceSelect
              initialSelection={initialSelection}
              onActiveDeviceChange={(deviceId) =>
                handleActiveDeviceChange(kind, deviceId)
              }
              onDeviceListChange={setDevices}
              kind={kind}
              track={tracks && tracks[kind]}
              requestPermissions={needPermissions}
            />
          ) : (
            <>
              <div className="lk-device-menu-heading">Audio inputs</div>
              <MediaDeviceSelect
                kind="audioinput"
                onActiveDeviceChange={(deviceId) =>
                  handleActiveDeviceChange('audioinput', deviceId)
                }
                onDeviceListChange={setDevices}
                track={tracks && tracks.audioinput}
                requestPermissions={needPermissions}
              />
              <div className="lk-device-menu-heading">Video inputs</div>
              <MediaDeviceSelect
                kind="videoinput"
                onActiveDeviceChange={(deviceId) =>
                  handleActiveDeviceChange('videoinput', deviceId)
                }
                onDeviceListChange={setDevices}
                track={tracks && tracks.videoinput}
                requestPermissions={needPermissions}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
